
  import { defineComponent, onMounted, ref, reactive, computed } from 'vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import moment from 'moment';
  import Multiselect from '@vueform/multiselect';
  import {
    TradeStatusMap,
    TradeStatusLabels,
  } from '../registered-trades/status-map.enum';
  import { StatusNotesLabels } from '../registered-trades/status-notes.enum';
  export default defineComponent({
    name: 'registered-trades-totals-reports',
    components: {
      Multiselect,
    },
    async setup() {
      const showExportBtn = ref(false);
      const data = ref();
      const isLoading = ref(false);
      const hasQueried = ref(false);
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const selectedItems = ref<any>({
        dateFrom: '',
        dateTo: '',
        areasIds: [],
        govIds: [],
        status: [],
        statusNotesIds: [],
        selectedGov: [],
        selectedPropertyStatus: [],
        propertyGroupingId: '',
      });
      const propertyGroupings = computed(
        () => store.getters.allListRegisteredTradesGroupings
      );
      const statusOptions = Object.entries(TradeStatusMap).map(
        ([label, value]) => ({ value, label })
      );
      const statusNotesOptions = Object.entries(StatusNotesLabels).map(
        ([value, label]) => ({ value: Number(value), label })
      );
      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };
      const goBack = () => {
        router.back();
      };
      const resetFilters = () => {
        selectedItems.value = {
          dateFrom: '',
          dateTo: '',
          areasIds: [],
          govIds: [],
          status: [],
          statusNotesIds: [],
          selectedGov: [],
          selectedPropertyStatus: [],
          propertyGroupingId: '',
        };
        showExportBtn.value = false;
        data.value = undefined;
        hasQueried.value = false;
      };
      // await store.dispatch(Actions.GET_REGISTERED_TRADE_REPORTS);
      // data.value = store.getters.registeredTradesDashboard;
      const onSubmitCreate = async () => {
        if (selectedItems.value.dateFrom) {
          selectedItems.value.dateFrom = moment(
            selectedItems.value.dateFrom
          ).format('YYYY-MM-DD');
        }
        if (selectedItems.value.dateTo) {
          selectedItems.value.dateTo = moment(
            selectedItems.value.dateTo
          ).format('YYYY-MM-DD');
        }
        if (selectedItems.value.dateFrom && selectedItems.value.dateTo) {
          if (
            moment(selectedItems.value.dateTo).isBefore(
              moment(selectedItems.value.dateFrom)
            )
          ) {
            Swal.fire({
              text: translate('dateToMustBeAfterDateFrom'),
              icon: 'warning',
              confirmButtonText: translate('ok'),
              buttonsStyling: false,
              customClass: { confirmButton: 'btn btn-light-primary' },
            });
            return;
          }
        }
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        // await store.dispatch(Actions.GET_REGISTERED_TRADE_REPORTS, values);
        // data.value = store.getters.registeredTradesReports;
        isLoading.value = true;
        hasQueried.value = true;
        await store.dispatch(
          Actions.GET_REGISTERED_TRADE_TOTALS_REPORTS,
          selectedItems
        );
        data.value = store.getters.registeredTradesReportsTotals;
        isLoading.value = false;
        if (data.value.data.length > 0) showExportBtn.value = true;
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];
        if (error) {
          isLoading.value = false;
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_GET_REPORTS_DATA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitButton.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
            submitButton.value!.disabled = false;
            // router.push({ name: 'ad-types-listing' });
          });
        }
      };
      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('RegisteredTradesTotalsReports'), [
          translate('Reports'),
        ]);
      });
      const govs = computed(() => store.getters.allListGov);
      const areas = computed(() => store.getters.allListAreas);
      const registeredTradesDescComp = computed(
        () => store.getters.getregisteredTradesDesc
      );
      const excelHeaderRows = computed(() => {
        const all = 'الكل';
        const rows: string[] = [];
        const from = selectedItems.value.dateFrom || '-';
        const to = selectedItems.value.dateTo || '-';
        rows.push(`التاريخ: من ${from} إلى ${to}`);

        if (selectedItems.value.selectedGov?.length > 0) {
          const labels = selectedItems.value.selectedGov
            .map((id) => {
              const g = govs.value?.find((gov) => gov.id === id);
              return g ? g.titleAr : id;
            })
            .join(', ');
          rows.push(`المحافظة: ${labels}`);
        } else {
          rows.push(`المحافظة: ${all}`);
        }

        if (selectedItems.value.propertyGroupingId) {
          const grouping = propertyGroupings.value.find(
            (g) => g.id === selectedItems.value.propertyGroupingId
          );
          rows.push(`تصنيف العقار: ${grouping ? grouping.titleAr : all}`);
        } else {
          rows.push(`تصنيف العقار: ${all}`);
        }

        if (selectedItems.value.selectedPropertyStatus?.length > 0) {
          rows.push(
            `حالة العقار: ${selectedItems.value.selectedPropertyStatus.join(
              ', '
            )}`
          );
        } else {
          rows.push(`حالة العقار: ${all}`);
        }

        if (selectedItems.value.propertyGroupingId) {
          const grouping = propertyGroupings.value.find(
            (g) => g.id === selectedItems.value.propertyGroupingId
          );
          rows.push(`تصنيف العقار: ${grouping ? grouping.titleAr : all}`);
        } else {
          rows.push(`تصنيف العقار: ${all}`);
        }

        if (selectedItems.value.status?.length > 0) {
          const labels = selectedItems.value.status
            .map((val) => TradeStatusLabels[val] || val)
            .join(', ');
          rows.push(`الحالة: ${labels}`);
        } else {
          rows.push(`الحالة: ${all}`);
        }

        if (selectedItems.value.statusNotesIds?.length > 0) {
          const labels = selectedItems.value.statusNotesIds
            .map((val) => StatusNotesLabels[val] || val)
            .join(', ');
          rows.push(`ملاحظات الحالة: ${labels}`);
        } else {
          rows.push(`ملاحظات الحالة: ${all}`);
        }

        return rows;
      });
      const excelHeader = reactive({
        area: 'govTitle',
        NumberOfTrades: 'count',
        TotalPrice: 'sum',
      });
      reinitializeComponents();
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      await store.dispatch(Actions.GET_ALL_AREAS_LIST, {
        type: 'registeredTrades',
      });
      await store.dispatch(Actions.GET_ALL_GOV_LIST);
      await store.dispatch(Actions.GET_REGISTERED_TRADES_DESC);
      await store.dispatch(Actions.GET_ALL_REGISTERED_TRADES_GROUPINGS);
      return {
        govs,
        areas,
        registeredTradesDesc: registeredTradesDescComp,
        excelHeader,
        excelHeaderRows,
        dateNow: new Date().toISOString().slice(0, 10),
        showExportBtn,
        data,
        selectedItems,
        onSubmitCreate,
        submitButton,
        translate,
        goBack,
        can,
        isLoading,
        hasQueried,
        resetFilters,
        statusOptions,
        statusNotesOptions,
        propertyGroupings,
      };
    },
  });
